/** Production → Purchasing → Vendors — name / contact / phone / email / address CRUD. */
import { useMemo, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { useManufacturingEntitlementGate } from "./useManufacturingEntitlementGate";
import { useManufacturingVendorsTab } from "./useManufacturingVendorsTab";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
  mfgSolidDangerBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgVendor } from "@/lib/manufacturing/purchaseOrderTypes";
import { VendorExtraContactsEditor } from "@/components/manufacturing/VendorExtraContactsEditor";
import { emptyVendorExtraContact, type MfgVendorExtraContact } from "@/lib/manufacturing/vendorExtraContacts";

type Draft = {
  name: string;
  primary_contact_name: string;
  phone: string;
  email: string;
  address: string;
  extra_contacts: MfgVendorExtraContact[];
};

const BLANK: Draft = {
  name: "",
  primary_contact_name: "",
  phone: "",
  email: "",
  address: "",
  extra_contacts: [emptyVendorExtraContact()],
};

export function ManufacturingVendorsPanel() {
  const gate = useManufacturingEntitlementGate();
  const [reloadTick, setReloadTick] = useState(0);
  const tab = useManufacturingVendorsTab(reloadTick);
  const [draft, setDraft] = useState<Draft>(BLANK);
  const [editingId, setEditingId] = useState<string | null>(null);

  const editing = useMemo(
    () => tab.vendors.find((v) => v.id === editingId) ?? null,
    [editingId, tab.vendors]
  );

  function startEdit(v: MfgVendor) {
    setEditingId(v.id);
    setDraft({
      name: v.name,
      primary_contact_name: v.primary_contact_name ?? "",
      phone: v.phone,
      email: v.email,
      address: v.address,
      extra_contacts: v.extra_contacts?.length
        ? v.extra_contacts
        : [emptyVendorExtraContact()],
    });
  }

  function clearForm() {
    setEditingId(null);
    setDraft(BLANK);
  }

  async function onSubmit() {
    if (!draft.name.trim()) {
      tab.setToast("Vendor name is required.");
      return;
    }
    const ok = editingId
      ? await tab.update({
          vendor_id: editingId,
          name: draft.name,
          primary_contact_name: draft.primary_contact_name,
          phone: draft.phone,
          email: draft.email,
          address: draft.address,
          extra_contacts: draft.extra_contacts,
        })
      : await tab.create(draft);
    if (ok) clearForm();
  }

  return (
    <div className="space-y-4">
      <div className="grid items-start gap-4 md:grid-cols-2">
      <SectionCard
        title={`Vendor list (${tab.vendors.length})`}
        rightSlot={
          <button
            type="button"
            className={ghostBtnClass}
            disabled={tab.busy || !gate.enabled}
            onClick={() => setReloadTick((n) => n + 1)}
          >
            Refresh
          </button>
        }
      >
        {tab.vendors.length === 0 ? (
          <p className="text-sm text-gray-500">No vendors yet.</p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
            {tab.vendors.map((v) => (
              <li key={v.id} className="flex flex-wrap items-start gap-2 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
                    {v.name}
                    {!v.is_active ? (
                      <span className="ml-2 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] uppercase dark:bg-zinc-700">
                        Inactive
                      </span>
                    ) : null}
                  </div>
                  {v.primary_contact_name ? (
                    <div className="mt-0.5 text-xs text-gray-700 dark:text-zinc-300">
                      Contact: {v.primary_contact_name}
                    </div>
                  ) : null}
                  <div className="mt-0.5 text-xs text-gray-600 dark:text-zinc-400">
                    {[v.phone, v.email].filter(Boolean).join(" · ") || "No phone/email"}
                  </div>
                  {(v.extra_contacts ?? []).length ? (
                    <div className="mt-0.5 text-xs text-gray-500">
                      {(v.extra_contacts ?? [])
                        .map((c) => (c.name ? `${c.name} · ${c.email}` : c.email))
                        .join(" · ")}
                    </div>
                  ) : null}
                  {v.address ? (
                    <div className="mt-0.5 whitespace-pre-wrap text-xs text-gray-500">
                      {v.address}
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={ghostBtnClass}
                    disabled={!gate.enabled || tab.busy}
                    onClick={() => startEdit(v)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className={ghostBtnClass}
                    disabled={!gate.enabled || tab.busy}
                    onClick={() =>
                      void tab.update({ vendor_id: v.id, is_active: !v.is_active })
                    }
                  >
                    {v.is_active ? "Deactivate" : "Reactivate"}
                  </button>
                  <button
                    type="button"
                    className={mfgSolidDangerBtnClass}
                    disabled={!gate.enabled || tab.busy}
                    onClick={() => {
                      if (
                        !window.confirm(
                          `Delete vendor "${v.name}"? Purchase orders that used it keep their snapshot; the vendor link is cleared.`
                        )
                      ) {
                        return;
                      }
                      void tab.remove(v.id).then((ok) => {
                        if (ok && editingId === v.id) clearForm();
                      });
                    }}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {editing ? (
          <p className="mt-2 text-xs text-indigo-700 dark:text-indigo-300">
            Editing {editing.name}
          </p>
        ) : null}
      </SectionCard>

      <SectionCard title={editingId ? "Edit vendor" : "Add vendor"}>
        <p className="text-xs text-gray-600 dark:text-zinc-300">
          Vendor contact info printed on purchase orders. Free-text vendor names on
          material aliases remain unchanged.
        </p>

        {tab.error ? (
          <div className="mt-2 rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-900/30 dark:text-rose-100">
            {tab.error}
          </div>
        ) : null}
        {tab.toast ? (
          <div className="mt-2 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-100">
            {tab.toast}
          </div>
        ) : null}

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <label className="text-xs sm:col-span-2">
            <span className="font-semibold">Name *</span>
            <input
              className={`${inputClass} mt-1 w-full`}
              value={draft.name}
              disabled={!gate.enabled || tab.busy}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
          </label>
          <label className="text-xs sm:col-span-2">
            <span className="font-semibold">Primary contact</span>
            <input
              className={`${inputClass} mt-1 w-full`}
              value={draft.primary_contact_name}
              disabled={!gate.enabled || tab.busy}
              onChange={(e) =>
                setDraft((d) => ({ ...d, primary_contact_name: e.target.value }))
              }
              placeholder="Name of main contact"
            />
          </label>
          <label className="text-xs">
            <span className="font-semibold">Phone</span>
            <input
              className={`${inputClass} mt-1 w-full`}
              value={draft.phone}
              disabled={!gate.enabled || tab.busy}
              onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
            />
          </label>
          <label className="text-xs">
            <span className="font-semibold">Email</span>
            <input
              className={`${inputClass} mt-1 w-full`}
              value={draft.email}
              disabled={!gate.enabled || tab.busy}
              onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
            />
          </label>
          <label className="text-xs sm:col-span-2">
            <span className="font-semibold">Address</span>
            <textarea
              className={`${inputClass} mt-1 w-full`}
              rows={2}
              value={draft.address}
              disabled={!gate.enabled || tab.busy}
              onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
            />
          </label>
          <VendorExtraContactsEditor
            contacts={draft.extra_contacts}
            onChange={(extra_contacts) => setDraft((d) => ({ ...d, extra_contacts }))}
            disabled={!gate.enabled || tab.busy}
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            className={mfgPrimaryBtnClass}
            disabled={!gate.enabled || tab.busy}
            onClick={() => void onSubmit()}
          >
            {editingId ? "Save vendor" : "+ Add vendor"}
          </button>
          {editingId ? (
            <button type="button" className={ghostBtnClass} onClick={clearForm}>
              Cancel edit
            </button>
          ) : null}
        </div>
      </SectionCard>
      </div>
    </div>
  );
}
